import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  useTheme,
  Typography,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Grid,
} from "@mui/material";
import Header from "../../components/VersatileComponents/Header";
import useFilteredCollectionData from "../../hooks/useFilteredCollectionData";
import {
  collection,
  query,
  where,
  getCountFromServer,
  getDocs,
  getFirestore,
} from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import getGrading from "../../api/services/grading/get_grading";
import editGrading from "../../api/services/grading/set_grading";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import useUserClaims from "../../hooks/useUserClaims";

const DeliveryGuyGrading = () => {
  const theme = useTheme();
  const db = getFirestore();
  const { id: branchId } = useParams();
  const { user } = useAuth();
  const { openSnackbar } = useSnackbar();
  const { setIsSubmitting } = React.useContext(SpinnerContext);
  const userClaims = useUserClaims(user);
  const { data: deliveryguys } = useFilteredCollectionData(
    "deliveryguy",
    "branchId",
    branchId || "nothing"
  );
  const [timeline, setTimeline] = useState("15days");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("total");
  const [gradingScales, setGradingScales] = useState(null);
  const [editMode, setEditMode] = useState({
    grade1: false,
    grade2: false,
    grade3: false,
    grade4: false,
    weights: false,
  });
  const [editValues, setEditValues] = useState({
    grade1: { grade: "", icon: "" },
    grade2: { grade: "", icon: "" },
    grade3: { grade: "", icon: "" },
    grade4: { grade: "", icon: "" },
    weights: {
      Asbeza: "",
      Card: "",
      CardFee: "",
      cardDistribute: "",
      hotelProfit: "",
      Water: "",
      waterDistribute: "",
      Wifi: "",
      wifiDistribute: "",
    },
  });

  // List of metrics in alphabetical orders
  const sortedMetrics = [
    "Asbeza",
    "Card",
    "CardFee",
    "cardDistribute",
    "hotelProfit",
    "Water",
    "waterDistribute",
    "Wifi",
    "wifiDistribute",
  ];

  // Fetch grading scales and weights
  const fetchGradingScales = useCallback(async () => {
    try {
      const gradingData = await getGrading(branchId);
      setGradingScales(gradingData);
      // Initialize weights in alphabetical order
      const weights = {};
      sortedMetrics.forEach((metric) => {
        weights[metric] = gradingData.weights?.[metric]?.toString() || "1";
      });
      setEditValues({
        grade1: {
          grade: gradingData.grade1.grade.toString(),
          icon: gradingData.grade1.icon,
        },
        grade2: {
          grade: gradingData.grade2.grade.toString(),
          icon: gradingData.grade2.icon,
        },
        grade3: {
          grade: gradingData.grade3.grade.toString(),
          icon: gradingData.grade3.icon,
        },
        grade4: {
          grade: gradingData.grade4.grade.toString(),
          icon: gradingData.grade4.icon,
        },
        weights,
      });
      // Show success message if default grading was created
      if (gradingData.id) {
        openSnackbar("Default grading scales created successfully! You can now customize them.", "success");
      }
    } catch (error) {
      console.error("Failed to fetch grading scales:", error);
      openSnackbar("Failed to fetch grading scales", "error");
    }
  }, [branchId, openSnackbar]);

  React.useEffect(() => {
    if (branchId) {
      fetchGradingScales();
    }
  }, [branchId, fetchGradingScales]);

  // Handle input change for editing thresholds, icons, and weights
  const handleInputChange = (key, field, value) => {
    setEditValues((prev) => {
      if (key === "weights") {
        return {
          ...prev,
          weights: {
            ...prev.weights,
            [field]: value,
          },
        };
      }
      return {
        ...prev,
        [key]: {
          ...prev[key],
          [field]: value,
        },
      };
    });
  };

  // Handle edit button click
  const handleEditButtonClick = (key) => {
    setEditMode((prev) => ({
      ...prev,
      [key]: true,
    }));
  };

  // Handle save button click
  const handleSaveButtonClick = async (key) => {
    let updates = {};
    if (key === "weights") {
      const weights = editValues.weights;
      for (const [metric, weight] of Object.entries(weights)) {
        if (
          weight === "" ||
          isNaN(parseFloat(weight)) ||
          parseFloat(weight) <= 0
        ) {
          openSnackbar(
            `Weight for ${metric} must be a positive number!`,
            "error"
          );
          setIsSubmitting(false);
          return;
        }
        updates[`weights.${metric}`] = parseFloat(weight);
      }
    } else {
      const { grade, icon } = editValues[key];
      if (grade === "" || isNaN(parseInt(grade))) {
        openSnackbar(
          `Grade ${key.toUpperCase()} threshold cannot be empty or invalid!`,
          "error"
        );
        setIsSubmitting(false);
        return;
      }
      if (icon === "") {
        openSnackbar(
          `Grade ${key.toUpperCase()} icon cannot be empty!`,
          "error"
        );
        setIsSubmitting(false);
        return;
      }
      updates[`${key}.grade`] = parseInt(grade, 10);
      updates[`${key}.icon`] = icon;
    }

    setIsSubmitting(true);
    setEditMode((prev) => ({
      ...prev,
      [key]: false,
    }));

    try {
      await editGrading(user, updates, branchId);
      openSnackbar(
        `${
          key === "weights" ? "Weights" : `Grade ${key.toUpperCase()}`
        } updated successfully!`,
        "success"
      );
      await fetchGradingScales();
    } catch (error) {
      openSnackbar(error.message || "An unexpected error occurred.", "error");
      setEditMode((prev) => ({
        ...prev,
        [key]: true,
      }));
    }
    setIsSubmitting(false);
  };

  // Calculate date range for timeline
  const getDateRange = () => {
    const now = new Date();
    let startDate;
    startDate = new Date(now);
    switch (timeline) {
      case "15days":
        startDate.setDate(startDate.getDate() - 15);
        break;
      case "1month":
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      case "6month":
        startDate.setMonth(startDate.getMonth() - 6);
        break;
      case "1year":
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
      default:
        startDate.setMonth(startDate.getMonth() - 1);
    }
    return startDate;
  };

  // Fetch order counts and additional metrics for a delivery guy
  const fetchOrderCounts = async (deliveryGuyId, startDate) => {
    const counts = {};
    const collections = [
      { name: "Asbeza", countField: "Asbeza" },
      { name: "Card", countField: "Card" },
      { name: "Water", countField: "Water" },
      { name: "Wifi", countField: "Wifi" },
      {
        name: "waterDistribute",
        countField: "waterDistribute",
        valueField: "numberOfCard",
      },
      {
        name: "wifiDistribute",
        countField: "wifiDistribute",
        valueField: "numberOfCard",
      },
      {
        name: "cardDistribute",
        countField: "cardDistribute",
        valueField: "numberOfCard",
      },
      {
        name: "hotelProfit",
        countField: "hotelProfit",
      },
      { name: "CardFee", countField: "CardFee", valueField: "numberOfCard" },
    ];

    for (const { name, countField, valueField } of collections) {
      const q = query(
        collection(db, name),
        where("deliveryguyId", "==", deliveryGuyId),
        where("createdAt", ">=", startDate)
      );
      if (!valueField) {
        const snapshot = await getCountFromServer(q);
        counts[countField] = snapshot.data().count;
      } else {
        const querySnapshot = await getDocs(q);
        let total = 0;
        querySnapshot.forEach((doc) => {
          total += doc.data()[valueField] || 0;
        });
        counts[countField] = total;
      }
    }
    return counts;
  };

  // Fetch counts and metrics for all delivery guys
  const [gradingData, setGradingData] = useState([]);
  React.useEffect(() => {
    const fetchAllCounts = async () => {
      const startDate = getDateRange();
      const data = await Promise.all(
        deliveryguys.map(async (guy) => {
          const counts = await fetchOrderCounts(guy.id, startDate);
          return {
            id: guy.id,
            name: guy.fullName || guy.id,
            ...counts,
          };
        })
      );
      setGradingData(data);
    };
    if (deliveryguys.length > 0) {
      fetchAllCounts();
    }
  }, [deliveryguys, timeline]);

  // Sorting logic
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    return gradingData.map((row, index) => ({ ...row, rank: index + 1 }));
  }, [gradingData]);

  // Apply weighted grading based on difference from average
  const gradedData = useMemo(() => {
    if (sortedData.length === 0 || !gradingScales) return sortedData;

    const weights = gradingScales.weights || {
      Asbeza: 1,
      Card: 1,
      Water: 1,
      Wifi: 1,
      hotelProfit: 1,
      cardDistribute: 1,
      CardFee: 1,
      waterDistribute: 1,
      wifiDistribute: 1,
    };

    const weightedData = sortedData.map((guy) => {
      const weightedSum =
        (guy.Asbeza || 0) * weights.Asbeza +
        (guy.Card || 0) * weights.Card +
        (guy.Water || 0) * weights.Water +
        (guy.Wifi || 0) * weights.Wifi +
        (guy.hotelProfit || 0) * weights.hotelProfit +
        (guy.cardDistribute || 0) * weights.cardDistribute +
        (guy.CardFee || 0) * weights.CardFee +
        (guy.waterDistribute || 0) * weights.waterDistribute +
        (guy.wifiDistribute || 0) * weights.wifiDistribute;

      return { ...guy, total: weightedSum };
    });

    const average =
      weightedData.reduce((acc, row) => acc + row.total, 0) /
      weightedData.length;

    const graded = weightedData.map((guy) => {
      const diffFromAverage = guy.total - average;
      let grade, icon;

      switch (true) {
        case diffFromAverage >= gradingScales.grade1.grade:
          grade = gradingScales.grade1.value;
          icon = gradingScales.grade1.icon;
          break;
        case diffFromAverage >= gradingScales.grade2.grade:
          grade = gradingScales.grade2.value;
          icon = gradingScales.grade2.icon;
          break;
        case diffFromAverage >= gradingScales.grade3.grade:
          grade = gradingScales.grade3.value;
          icon = gradingScales.grade3.icon;
          break;
        case diffFromAverage >= gradingScales.grade4.grade:
          grade = gradingScales.grade4.value;
          icon = gradingScales.grade4.icon;
          break;
        default:
          grade = "F";
          icon = "❌";
      }

      return {
        ...guy,
        total: guy.total.toFixed(2),
        diffFromAverage: diffFromAverage.toFixed(2),
        grade,
        icon,
      };
    });

    // Apply sorting
    const sorted = [...graded].sort((a, b) => {
      const valueA = a[orderBy] || (orderBy === "name" ? "" : 0);
      const valueB = b[orderBy] || (orderBy === "name" ? "" : 0);
      if (orderBy === "name") {
        return order === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      return order === "asc"
        ? parseFloat(valueA) - parseFloat(valueB)
        : parseFloat(valueB) - parseFloat(valueA);
    });

    return sorted.map((row, index) => ({ ...row, rank: index + 1 }));
  }, [sortedData, gradingScales, order, orderBy]);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Delivery Guy Grading</title>
        <meta
          name="description"
          content="Ranking of delivery guys by weighted order counts and metrics"
        />
      </Helmet>
      <Box
        m="1.5rem 2.5rem"
        sx={{
          backgroundColor: theme.palette.background.default,
          height: "100%",
          position: "relative",
        }}
      >
        <Header title="Delivery Guy Grading" />
        {gradingScales && (
          <Box sx={{ mt: 2, mb: 4 }}>
            <Typography variant="h6" color={theme.palette.secondary[50]}>
              Grading Scales (Difference from Average Weighted Score)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <List>
                  {[
                    { key: "grade1", data: gradingScales.grade1 },
                    { key: "grade2", data: gradingScales.grade2 },
                    { key: "grade3", data: gradingScales.grade3 },
                    { key: "grade4", data: gradingScales.grade4 },
                  ].map(({ key, data }) => (
                    <ListItem key={key}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={8}>
                          <ListItemText
                            primary={
                              editMode[key] ? (
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <TextField
                                    value={editValues[key].icon}
                                    onChange={(e) =>
                                      handleInputChange(
                                        key,
                                        "icon",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    sx={{ width: "60px" }}
                                    placeholder="Icon"
                                    disabled={
                                      !(
                                        userClaims.superAdmin ||
                                        userClaims.admin
                                      )
                                    }
                                  />
                                  <TextField
                                    value={editValues[key].grade}
                                    type="number"
                                    onChange={(e) =>
                                      handleInputChange(
                                        key,
                                        "grade",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    sx={{ width: "100px" }}
                                    placeholder="Threshold"
                                    disabled={
                                      !(
                                        userClaims.superAdmin ||
                                        userClaims.admin
                                      )
                                    }
                                  />
                                </Box>
                              ) : (
                                `${data.icon} Grade ${data.value}: ≥ ${data.grade}`
                              )
                            }
                            sx={{ color: theme.palette.secondary[50] }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          {(userClaims.superAdmin || userClaims.admin) &&
                            (editMode[key] ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleSaveButtonClick(key)}
                              >
                                Save
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleEditButtonClick(key)}
                              >
                                Edit
                              </Button>
                            ))}
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                  <ListItem>
                    <ListItemText
                      primary="❌ Grade F: < -200"
                      sx={{ color: theme.palette.secondary[50] }}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle1"
                  color={theme.palette.secondary[50]}
                >
                  Weights
                </Typography>
                <List>
                  <ListItem>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={8}>
                        <ListItemText
                          primary={
                            editMode.weights ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                {sortedMetrics.map((metric) => (
                                  <Box
                                    key={metric}
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography sx={{ width: "120px" }}>
                                      {metric}:
                                    </Typography>
                                    <TextField
                                      value={editValues.weights[metric]}
                                      type="number"
                                      onChange={(e) =>
                                        handleInputChange(
                                          "weights",
                                          metric,
                                          e.target.value
                                        )
                                      }
                                      size="small"
                                      sx={{ width: "80px" }}
                                      placeholder="Weight"
                                      inputProps={{ step: "0.1", min: "0" }}
                                      disabled={
                                        !(
                                          userClaims.superAdmin ||
                                          userClaims.admin
                                        )
                                      }
                                    />
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                {sortedMetrics.map((metric) => (
                                  <Typography key={metric}>{`${metric}: ${
                                    gradingScales.weights?.[metric] || 1
                                  }`}</Typography>
                                ))}
                              </Box>
                            )
                          }
                          sx={{ color: theme.palette.secondary[50] }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        {(userClaims.superAdmin || userClaims.admin) &&
                          (editMode.weights ? (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSaveButtonClick("weights")}
                            >
                              Save
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleEditButtonClick("weights")}
                            >
                              Edit
                            </Button>
                          ))}
                      </Grid>
                    </Grid>
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Box>
        )}
        <FormControl sx={{ mt: 4, mb: 2, minWidth: 120 }}>
          <InputLabel>Timeline</InputLabel>
          <Select
            value={timeline}
            label="Timeline"
            onChange={(e) => setTimeline(e.target.value)}
            sx={{
              color: theme.palette.secondary[200],
              backgroundColor: theme.palette.background.alt,
            }}
          >
            <MenuItem value="15days">15 Days</MenuItem>
            <MenuItem value="1month">1 Month</MenuItem>
            <MenuItem value="6month">6 Months</MenuItem>
            <MenuItem value="1year">1 Year</MenuItem>
          </Select>
        </FormControl>
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[200],
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#323E8B" : "#C5C7D7"
            }`,
            borderRadius: ".1rem",
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: theme.palette.background.alt,
                }}
              >
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  Rank
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => handleRequestSort("name")}
                  >
                    Delivery Guy
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Asbeza"}
                    direction={orderBy === "Asbeza" ? order : "desc"}
                    onClick={() => handleRequestSort("Asbeza")}
                  >
                    Asbeza
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Card"}
                    direction={orderBy === "Card" ? order : "desc"}
                    onClick={() => handleRequestSort("Card")}
                  >
                    Card
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Water"}
                    direction={orderBy === "Water" ? order : "desc"}
                    onClick={() => handleRequestSort("Water")}
                  >
                    Water
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "Wifi"}
                    direction={orderBy === "Wifi" ? order : "desc"}
                    onClick={() => handleRequestSort("Wifi")}
                  >
                    Wifi
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "hotelProfit"}
                    direction={orderBy === "hotelProfit" ? order : "desc"}
                    onClick={() => handleRequestSort("hotelProfit")}
                  >
                    Hotel Profit
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "cardDistribute"}
                    direction={orderBy === "cardDistribute" ? order : "desc"}
                    onClick={() => handleRequestSort("cardDistribute")}
                  >
                    Card Distribute
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "CardFee"}
                    direction={orderBy === "CardFee" ? order : "desc"}
                    onClick={() => handleRequestSort("CardFee")}
                  >
                    Card Fee
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "waterDistribute"}
                    direction={orderBy === "waterDistribute" ? order : "desc"}
                    onClick={() => handleRequestSort("waterDistribute")}
                  >
                    Water Distribute
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "wifiDistribute"}
                    direction={orderBy === "wifiDistribute" ? order : "desc"}
                    onClick={() => handleRequestSort("wifiDistribute")}
                  >
                    Wifi Distribute
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === "total"}
                    direction={orderBy === "total" ? order : "desc"}
                    onClick={() => handleRequestSort("total")}
                  >
                    Total
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  Grade
                </TableCell>
                <TableCell
                  sx={{
                    color: theme.palette.secondary[50],
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gradedData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#333964" : "white",
                    borderBottom: `1px solid ${
                      theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                    }`,
                  }}
                >
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.rank}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.name}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.Asbeza || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.Card || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.Water || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.Wifi || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.hotelProfit || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.cardDistribute || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.CardFee || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.waterDistribute || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.wifiDistribute || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.total || 0}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.grade}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.secondary[50] }}>
                    {row.icon}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default DeliveryGuyGrading;
